import styled from "styled-components";
import {Person, PromptResult, PromptStyle, PromptTemplate, StyledBaseProps} from "@betrue/shared/interfaces";
import * as React from "react";
import {useEffect, useState} from "react";
import {Alert, Grid, IconButton, Snackbar, Stack, useMediaQuery, useTheme} from "@mui/material";
import {LogoEditorProps} from "./logo-editor";
import Paper from '@mui/material/Paper';
import InputBase from '@mui/material/InputBase';
import Divider from '@mui/material/Divider';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import {LogoPlaceholderView} from "./logo-placeholder.view";
import {LogoCardView} from "./logo-card.view";
import FileUploadIcon from '@mui/icons-material/FileUpload';
import {ProfileAvatarEditorView} from "../profile-avatar-editor/profile-avatar-editor.view";
import DeleteIcon from '@mui/icons-material/Delete';
import GestureIcon from '@mui/icons-material/Gesture';
import {Image, ImageBackdrop, ImageButton, ImageMarked, ImageSrc} from "./image-button";
import {DialogBaseView} from "../dialog/dialog.view";
import {LogoDrawerView} from "./logo-drawer.view";
import {LogoSettingsView} from "./logo-settings.view";
import SettingsApplicationsIcon from '@mui/icons-material/SettingsApplications';

interface LogoEditorViewProps extends StyledBaseProps, LogoEditorProps {
  generatedLogos: PromptResult[]
  handleGenerate: (prompt: string) => void
  chronos: number
  promptStyles: PromptTemplate[]
  selectedPromptTemplateId: string
  handleSelectedPromptTemplateId: (id: string) => void
  handleModelChanged: (model: string) => void
  model: string
  principal: Person
  handleErrorClose: () => void
  errorOpen: boolean
  imageRef: string
  setImageRef: (image: string) => void
  inputWeight: number
  setInputWeight: (weight: number) => void
}


export const LogoEditorView = styled((props: LogoEditorViewProps) => {

  const [rawPrompt, setRawPrompt] = useState('')
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up('md'));
  const [logoUploadOpen, setLogoUploadOpen] = useState(false)
  const [drawerIsOpen, setDrawerIsOpen] = useState(false)
  const [settingsIsOpen, setSettingsIsOpen] = useState(false)

  useEffect(() => {
    console.log(matches)
  }, [matches, theme])

  return (
    <div className={props.className}>

      <Grid container direction={'row'} spacing={matches ? 2 : 0}
            style={{height: '100%', width: '100%', position: 'relative'}}>
        {matches ? (<Grid item md={4} xs={12}>
          <LogoSettingsView
            inputWeight={props.inputWeight}
            setInputWeight={props.setInputWeight}
            model={props.model}
            principal={props.principal}
            handleModelChanged={props.handleModelChanged}
            promptStyles={props.promptStyles}
            selectedPromptTemplateId={props.selectedPromptTemplateId}
            handleSelectedPromptTemplateId={props.handleSelectedPromptTemplateId}/>
        </Grid>) : (<Grid item xs={12}>
          <Grid container alignContent={'center'} direction={'column'} alignItems={'center'}>
            <Grid item>
              <Button variant="outlined" onClick={() => setSettingsIsOpen(true)}
                      startIcon={<SettingsApplicationsIcon/>}>
                Paramètres
              </Button><DialogBaseView
              dialogSave={'Valider'}
              dialogTitle={'Paramètres'}
              dialogCancel={'Fermer'}
              dialogSubtitle={'Choisissez un model'}
              isOpen={settingsIsOpen}
              handleClose={() => setSettingsIsOpen(false)}
              handleSubmit={() => setSettingsIsOpen(false)}>
              <LogoSettingsView
                inputWeight={props.inputWeight}
                setInputWeight={props.setInputWeight}
                model={props.model}
                principal={props.principal}
                handleModelChanged={props.handleModelChanged}
                promptStyles={props.promptStyles}
                selectedPromptTemplateId={props.selectedPromptTemplateId}
                handleSelectedPromptTemplateId={props.handleSelectedPromptTemplateId}/>
            </DialogBaseView>
            </Grid>
          </Grid>
        </Grid>)}
        <Grid item md={8} xs={12} style={{height: '100%', position: 'relative'}}>
          <Grid container spacing={3} style={{marginTop: matches ? '0' : '10px', height: '100%', overflow: 'scroll'}}>
            <Grid item md={6} xs={12}>
              <LogoPlaceholderView chronos={props.chronos}/>
            </Grid>
            {props.generatedLogos?.sort((l1,
                                         l2) => new Date(l2.creationDate)?.getTime() - new Date(l1.creationDate).getTime()).map(logo => (
              <Grid key={logo.id} item md={6} xs={12}>
                <LogoCardView
                  principal={props.principal}
                  logoImage={logo}/>
              </Grid>))}
          </Grid>

          <Paper
            className={'prompt-tools'}
            component="form">

            {props.imageRef ? (
                <ImageButton
                  focusRipple
                  onClick={() => props.setImageRef(null)}
                  style={{
                    width: 60,
                  }}
                >
                  <ImageSrc style={{backgroundImage: `url(${props.imageRef})`}}/>
                  <ImageBackdrop className="MuiImageBackdrop-root"/>
                  <Image>
                    <Typography
                      component="span"
                      variant="subtitle1"
                      color="inherit"
                      sx={{
                        position: 'relative',
                        p: 4,
                        pt: 2,
                        pb: (theme) => `calc(${theme.spacing(1)} + 6px)`,
                      }}
                    >
                      <DeleteIcon/>
                      <ImageMarked className="MuiImageMarked-root"/>
                    </Typography>
                  </Image>
                </ImageButton>) :
              (<Stack direction={'row'}>
                <IconButton
                  onClick={() => setLogoUploadOpen(true)}>
                  <FileUploadIcon/>
                </IconButton>
                <IconButton
                  disabled={!!props.chronos || !props.selectedPromptTemplateId}
                  onClick={() => setDrawerIsOpen(true)}>
                  <GestureIcon/>
                </IconButton>
              </Stack>)}


            <Divider sx={{height: 40, m: 0.5}} orientation="vertical"/>

            <InputBase
              sx={{ml: 1, flex: 1}}
              multiline={true}
              rows={4}
              value={rawPrompt}
              onChange={(e) => setRawPrompt(e.target.value)}
              placeholder="Votre Prompt en français :) !"
              inputProps={{'aria-label': 'Ajoutez ici votre texte, décrivant le contenu que vous souhaitez générer.'}}
            />
            <Divider sx={{height: 40, m: 0.5}} orientation="vertical"/>
            <Button disabled={!!props.chronos || !props.selectedPromptTemplateId} onClick={() => props.handleGenerate(rawPrompt)}
                    variant={'text'}
                    startIcon={<AutoFixHighIcon/>}>Imagine</Button>

          </Paper>
        </Grid>
      </Grid>
      <DialogBaseView handleClose={() => {
        props.setImageRef('')
        setDrawerIsOpen(false)
      }}
                      handleSubmit={() => {
                        setDrawerIsOpen(false);
                      }}
                      isOpen={drawerIsOpen}
                      dialogTitle={"Dessiner votre logo"}
                      dialogSubtitle={"laisser vous guider"}
                      dialogCancel={'Annuler'}
                      dialogSave={'Valider'}>
        <LogoDrawerView
          canvasHeight={1024}
          radius={6}
          canvasWidth={1024}
          onChange={(image) => props.setImageRef(image)}
        />

      </DialogBaseView>
      <ProfileAvatarEditorView
        onClose={() => setLogoUploadOpen(false)}
        modalOpen={logoUploadOpen}
        width={1024}
        height={1024}
        avatarLabel={"Votre logo"}
        currentImage={''}
        dialogTitle={'Transformez votre logo'}
        dialogSubtitle={'Modifiez un logo existant pour transformer une esquisse'}
        onSubmit={(image) => {
          props.setImageRef(image);
          setLogoUploadOpen(false);
        }}/>
      <Snackbar open={props.errorOpen} autoHideDuration={6000} onClose={props.handleErrorClose}>
        <Alert onClose={props.handleErrorClose} severity="error" sx={{width: '100%'}}>
          Oups une erreur s'est produite :'( si cela persiste veuillez réessayer plus tard.
        </Alert>
      </Snackbar>

    </div>
  )
})`
  & {


  }

  height: 100%;

  .selected-style {
    border: solid 3px #690038;
  }

  .style-card {
    height: 10px;
    padding: 5px;
    text-align: center;
    padding-bottom: 15px !important;
  }

  .vertical-align {
    position: absolute;
    text-align: center;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  .prompt-tools {
    display: flex;
    position: absolute;
    padding: 2px 4px;
    align-items: center;
    width: 90%;
    right: 50%;
    left: 50%;
    bottom: 0;
    transform: translate(-50%, -50%);

  }

`
